import React, { useCallback, useEffect, useState, useRef } from 'react';
import styled from 'styled-components/macro';
import translate from './TranslateHOC';
import CardGrid from './CardGrid';
import { HeroCard as HeroCardV2 } from '../v2/components/hero/heroCard/HeroCard';
import SectionTitle from './SectionTitle';
import { PLAY_NEXT_DELAY_MS } from '../constants';
import { rgba } from 'polished';
import { device } from '../v2/Theme';
import { BASE_URL } from '../constants';

function ProgrammePlayNext({
  auth,
  items,
  strings,
  moreItems,
  clearTimer,
  isAutoPlay,
  isProgramme,
  history,
}) {
  const [isCancelled, setIsCancelled] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(0);
  const [startTimer, setStartTimer] = useState(false);

  const heroCardRef = useRef(null);

  useEffect(() => {
    setCountdownTimer(PLAY_NEXT_DELAY_MS / 1000);
    setStartTimer(true);
  }, []);

  useEffect(() => {
    if (countdownTimer > 0) {
      const timerId = setTimeout(() => {
        setCountdownTimer(countdownTimer - 1);
      }, 1000);

      return () => {
        clearTimeout(timerId);
      };
    }

    if (countdownTimer === 0 && startTimer) {
      setStartTimer(false);
    }
  }, [countdownTimer, startTimer]);

  const handleCancel = useCallback(() => {
    setIsCancelled(true);
    clearTimer();
    setStartTimer(false);
  }, [clearTimer]);

  return (
    <ProgrammePlayNextContainer>
      <ProgrammePlayNextInner
        ref={heroCardRef}
        style={{
          backgroundImage:
            items.thumbnail &&
            `url(${BASE_URL}amg/landscape/726x564/${items.thumbnail})`,
        }}
      >
        <PlayNext
          programmeNext={Object.keys(items).length > 0}
          recommended={Object.keys(moreItems).length > 0}
        >
          {Object.keys(items).length > 0 && (
            <HeroCardV2
              details={items}
              auth={auth}
              strings={strings}
              handleCancel={handleCancel}
              isCancelled={isCancelled}
              isProgramme={isProgramme}
              isAutoPlay={isAutoPlay}
              timer={countdownTimer}
              programmeNext={Object.keys(items).length > 0}
              recommended={Object.keys(moreItems).length > 0}
              noImage={true}
              history={history}
            />
          )}
          {Object.keys(moreItems).length > 0 && (
            <CardGridContainer
              isProgramme={isProgramme}
              programmeNext={Object.keys(items).length > 0}
              recommended={Object.keys(moreItems).length > 0}
            >
              <SectionTitle type="isProgramme">
                {strings.recommended}
              </SectionTitle>
              <CardGrid
                gridType="isProgramme"
                recommended={
                  !Object.keys(items).length > 0 &&
                  Object.keys(moreItems).length > 0
                }
                items={moreItems}
                playNow={isAutoPlay}
              />
            </CardGridContainer>
          )}
        </PlayNext>
      </ProgrammePlayNextInner>
    </ProgrammePlayNextContainer>
  );
}

const ProgrammePlayNextContainer = styled.div`
  position: relative;
  left: 50%;
  position: absolute;
  top: 0;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  width: 100%;
  z-index: 100;
  border-radius: 12px;
`;

const ProgrammePlayNextInner = styled.div`
  height: 0;
  left: 0;
  padding-bottom: 56.25%;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 20;
  background-size: cover;
  background-repeat: no-repeat;
  @media ${device.tablet} {
    border: 1px solid
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.white75
          : props.theme.color.black75};
    border-radius: 12px;
  }
  &:before {
    background: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.whiteFull
        : props.theme.color.blackFull};
    content: '';
    display: block;
    height: 100%;
    opacity: 0;
    position: absolute;
    top: 0;
    width: 100%;
    transition: opacity 75ms ease;
  }
  &:after {
    background: ${(props) =>
      props.theme.name === 'light'
        ? `
        linear-gradient(180deg, ${rgba(
          props.theme.color.whiteFull,
          0.15
        )} 0%, ${rgba(props.theme.color.whiteFull, 0.15735)} 6.67%, ${rgba(
            props.theme.color.whiteFull,
            0.180184
          )} 13.33%, ${rgba(props.theme.color.whiteFull, 0.219411)} 20%, ${rgba(
            props.theme.color.whiteFull,
            0.2753
          )} 26.67%, ${rgba(
            props.theme.color.whiteFull,
            0.347009
          )} 33.33%, ${rgba(props.theme.color.whiteFull, 0.432101)} 40%, ${rgba(
            props.theme.color.whiteFull,
            0.526288
          )} 46.67%, ${rgba(
            props.theme.color.whiteFull,
            0.623712
          )} 53.33%, ${rgba(props.theme.color.whiteFull, 0.717899)} 60%, ${rgba(
            props.theme.color.whiteFull,
            0.802991
          )} 66.67%, ${rgba(
            props.theme.color.whiteFull,
            0.8747
          )} 73.33%, ${rgba(props.theme.color.whiteFull, 0.930589)} 80%, ${rgba(
            props.theme.color.whiteFull,
            0.969817
          )} 86.67%, ${rgba(props.theme.color.whiteFull, 0.99265)} 93.33%, ${
            props.theme.color.whiteFull
          } 100%),
        linear-gradient(256.01deg, ${rgba(
          props.theme.color.whiteFull,
          0.075
        )} 44.8%, ${rgba(props.theme.color.whiteFull, 0.078675)} 47.31%, ${rgba(
            props.theme.color.whiteFull,
            0.090092
          )} 49.81%, ${rgba(
            props.theme.color.whiteFull,
            0.1097055
          )} 52.32%, ${rgba(
            props.theme.color.whiteFull,
            0.13765
          )} 54.82%, ${rgba(
            props.theme.color.whiteFull,
            0.1735045
          )} 57.33%, ${rgba(
            props.theme.color.whiteFull,
            0.2160505
          )} 59.83%, ${rgba(
            props.theme.color.whiteFull,
            0.263144
          )} 62.34%, ${rgba(
            props.theme.color.whiteFull,
            0.311856
          )} 64.84%, ${rgba(
            props.theme.color.whiteFull,
            0.3589495
          )} 67.35%, ${rgba(
            props.theme.color.whiteFull,
            0.4014955
          )} 69.85%, ${rgba(
            props.theme.color.whiteFull,
            0.43735
          )} 72.36%, ${rgba(
            props.theme.color.whiteFull,
            0.4652945
          )} 74.86%, ${rgba(
            props.theme.color.whiteFull,
            0.4849085
          )} 77.37%, ${rgba(props.theme.color.whiteFull, 0.496325)} 79.87%, ${
            props.theme.color.whiteFull
          } 82.38%),
        linear-gradient(89.96deg, ${props.theme.color.whiteFull} 1.34%, ${rgba(
            props.theme.color.whiteFull,
            0
          )} 50.03%)
      `
        : `
        linear-gradient(180deg, ${rgba(
          props.theme.color.blackFull,
          0.15
        )} 0%, ${rgba(props.theme.color.blackFull, 0.15735)} 6.67%, ${rgba(
            props.theme.color.blackFull,
            0.180184
          )} 13.33%, ${rgba(props.theme.color.blackFull, 0.219411)} 20%, ${rgba(
            props.theme.color.blackFull,
            0.2753
          )} 26.67%, ${rgba(
            props.theme.color.blackFull,
            0.347009
          )} 33.33%, ${rgba(props.theme.color.blackFull, 0.432101)} 40%, ${rgba(
            props.theme.color.blackFull,
            0.526288
          )} 46.67%, ${rgba(
            props.theme.color.blackFull,
            0.623712
          )} 53.33%, ${rgba(props.theme.color.blackFull, 0.717899)} 60%, ${rgba(
            props.theme.color.blackFull,
            0.802991
          )} 66.67%, ${rgba(
            props.theme.color.blackFull,
            0.8747
          )} 73.33%, ${rgba(props.theme.color.blackFull, 0.930589)} 80%, ${rgba(
            props.theme.color.blackFull,
            0.969817
          )} 86.67%, ${rgba(props.theme.color.blackFull, 0.99265)} 93.33%, ${
            props.theme.color.blackFull
          } 100%),
        linear-gradient(256.01deg, ${rgba(
          props.theme.color.blackFull,
          0.075
        )} 44.8%, ${rgba(props.theme.color.blackFull, 0.078675)} 47.31%, ${rgba(
            props.theme.color.blackFull,
            0.090092
          )} 49.81%, ${rgba(
            props.theme.color.blackFull,
            0.1097055
          )} 52.32%, ${rgba(
            props.theme.color.blackFull,
            0.13765
          )} 54.82%, ${rgba(
            props.theme.color.blackFull,
            0.1735045
          )} 57.33%, ${rgba(
            props.theme.color.blackFull,
            0.2160505
          )} 59.83%, ${rgba(
            props.theme.color.blackFull,
            0.263144
          )} 62.34%, ${rgba(
            props.theme.color.blackFull,
            0.311856
          )} 64.84%, ${rgba(
            props.theme.color.blackFull,
            0.3589495
          )} 67.35%, ${rgba(
            props.theme.color.blackFull,
            0.4014955
          )} 69.85%, ${rgba(
            props.theme.color.blackFull,
            0.43735
          )} 72.36%, ${rgba(
            props.theme.color.blackFull,
            0.4652945
          )} 74.86%, ${rgba(
            props.theme.color.blackFull,
            0.4849085
          )} 77.37%, ${rgba(props.theme.color.blackFull, 0.496325)} 79.87%, ${
            props.theme.color.blackFull
          } 82.38%),
        linear-gradient(89.96deg, ${props.theme.color.blackFull} 1.34%, ${rgba(
            props.theme.color.blackFull,
            0
          )} 50.03%)
      `};
    display: block;
    height: 100%;
    position: absolute;
    top: 0;
    width: 300%;
    content: '';
    @media ${device.tablet} {
      border-radius: 12px;
      width: 250%;
    }
  }
`;

const PlayNext = styled.div`
  display: flex;
  position: absolute;
  width: 100%;
  bottom: 0;
  flex-direction: row;
  align-items: flex-end;
  @media ${device.tablet} {
    > * {
      flex: 1 1 0px;
    }
  }
  @media ${device.desktop} {
    display: block;
  }
  @media ${device.widest} {
    ${(props) =>
      props.programmeNext &&
      props.recommended &&
      `align-items: initial;
      flex-direction: column;
    `};
  }
`;

const CardGridContainer = styled.section`
  position: relative;
  z-index: 10;
  ${(props) =>
    props.recommended &&
    `
      width: 100%;
  `};
  ${(props) => props.programmeNext && props.recommended && `width: auto;`};
  @media screen and (min-width: 640px) {
    ${(props) =>
      props.isProgramme &&
      `
      margin-left: auto;
      width: 50%;
  `};
    ${(props) =>
      props.recommended &&
      `
      margin-left: 0;
  `};
  }
  @media ${device.tablet} {
    margin-left: 0;
    padding-left: 0;
    ${(props) =>
      props.isProgramme &&
      `
        padding: 0 25px 25px;
    `};
    ${(props) =>
      props.recommended &&
      `
        width: 100%;
    `};
  }
  @media ${device.desktop} {
    width: auto;
  }
  @media ${device.widest} {
    ${(props) => props.isProgramme && props.recommended && `flex-basis: 0;`};
    ${(props) =>
      props.isProgramme &&
      `
        padding: 0 50px 20px;
    `};
  }
  & + & {
    box-shadow: inset 0 1px 0 0
      ${(props) =>
        props.theme.name === 'light'
          ? rgba(props.theme.color.blackFull, 0.25)
          : props.theme.color.white10};
  }
`;

export default translate(ProgrammePlayNext);
